/**
 * Fees hooks using React Relay
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  useLazyLoadQuery,
  usePaginationFragment,
  fetchQuery,
} from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  useFeesRelayQuery as UseFeesRelayQuery,
  useFeesRelayQuery$variables,
  FeeFilterInput,
  FeeSortInput,
} from './__generated__/useFeesRelayQuery.graphql';
import type { useFeesRelayDirectQuery as UseFeesRelayDirectQuery } from './__generated__/useFeesRelayDirectQuery.graphql';
import type { useFeesRelayByIdQuery } from './__generated__/useFeesRelayByIdQuery.graphql';
import type { useFeesRelayPaginationFragment$key } from './__generated__/useFeesRelayPaginationFragment.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

const FeesListPaginationFragment = graphql`
  fragment useFeesRelayPaginationFragment on RootQueryType
  @refetchable(queryName: "useFeesRelayPaginationQuery")
  @argumentDefinitions(
    first: { type: "Int", defaultValue: 100 }
    after: { type: "String" }
    filter: { type: "FeeFilterInput" }
    sort: { type: "[FeeSortInput!]" }
  ) {
    listFees(first: $first, after: $after, filter: $filter, sort: $sort)
      @connection(
        key: "useFeesRelayPaginationFragment_listFees"
        filters: ["filter", "sort"]
      ) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          name
          amount
          description
          activeOn
          archivedOn
          appliedPostTax
          appliedToMembers
          appliedAboveSubTotalThreshold
          appliedUnderSubTotalThreshold
          systemId
          organizationId
          createdOn
          system {
            id
            oldId
            name
          }
        }
      }
    }
  }
`;

const FeesListQuery = graphql`
  query useFeesRelayQuery(
    $first: Int
    $after: String
    $filter: FeeFilterInput
    $sort: [FeeSortInput!]
  ) {
    ...useFeesRelayPaginationFragment
      @arguments(first: $first, after: $after, filter: $filter, sort: $sort)
  }
`;

const FeesListDirectQuery = graphql`
  query useFeesRelayDirectQuery(
    $first: Int
    $after: String
    $filter: FeeFilterInput
    $sort: [FeeSortInput!]
  ) {
    listFees(first: $first, after: $after, filter: $filter, sort: $sort) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          name
          amount
          description
          activeOn
          archivedOn
          appliedPostTax
          appliedToMembers
          appliedAboveSubTotalThreshold
          appliedUnderSubTotalThreshold
          systemId
          organizationId
          createdOn
          system {
            id
            oldId
            name
          }
        }
      }
    }
  }
`;

const FeeByIdQuery = graphql`
  query useFeesRelayByIdQuery($id: ID!) {
    getFee(id: $id) {
      id
      name
      amount
      description
      activeOn
      archivedOn
      appliedPostTax
      appliedToMembers
      appliedAboveSubTotalThreshold
      appliedUnderSubTotalThreshold
      systemId
      organizationId
      createdOn
      system {
        id
        oldId
        name
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export type { FeeFilterInput, FeeSortInput };

export interface FeeSystemGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
}

export interface FeeGraphQL {
  id: string;
  name: string;
  amount: number;
  description?: string | null;
  activeOn: string;
  archivedOn?: string | null;
  appliedPostTax: boolean;
  appliedToMembers: boolean;
  appliedAboveSubTotalThreshold?: number | null;
  appliedUnderSubTotalThreshold?: number | null;
  systemId: string;
  organizationId?: string | null;
  createdOn: string;
  system?: FeeSystemGraphQL | null;
}

export interface FeeQueryFilter {
  systemId?: string;
  organizationId?: string;
  name?: string;
  archivedOn?: { isNil?: boolean };
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Suspense-based hook for fetching fees list
 */
export function useFeesRelay(
  filter?: FeeQueryFilter,
  options?: {
    limit?: number;
    sort?: FeeSortInput[];
  }
) {
  const limit = options?.limit ?? 100;
  const sort = options?.sort ?? [{ field: 'NAME', order: 'ASC' }];

  const variables: useFeesRelayQuery$variables = {
    first: limit,
    filter: buildRelayFilter(filter),
    sort,
  };

  const queryData = useLazyLoadQuery<UseFeesRelayQuery>(FeesListQuery, variables);

  const { data, loadNext, hasNext, isLoadingNext, refetch } = usePaginationFragment<
    UseFeesRelayQuery,
    useFeesRelayPaginationFragment$key
  >(FeesListPaginationFragment, queryData);

  const nodes =
    data.listFees?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];

  return {
    data: nodes as FeeGraphQL[],
    count: data.listFees?.count,
    pageInfo: data.listFees?.pageInfo,
    loadNext,
    hasNext,
    isLoadingNext,
    refetch,
  };
}

/**
 * Suspense-based hook for fetching a single fee by ID
 */
export function useFeeByIdRelay(feeId: string) {
  const data = useLazyLoadQuery<useFeesRelayByIdQuery>(FeeByIdQuery, {
    id: feeId,
  });

  return data.getFee as FeeGraphQL | null;
}

// ============================================================================
// React Query-like Interface (No Suspense)
// ============================================================================

/**
 * Hook that provides React Query-like interface for fees data
 */
export function useFeesRelayQuery(
  filter?: FeeQueryFilter,
  options?: {
    limit?: number;
    sort?: FeeSortInput[];
    enabled?: boolean;
  }
) {
  const [data, setData] = useState<FeeGraphQL[] | null>(null);
  const [count, setCount] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const limit = options?.limit ?? 100;
  const sort = useMemo(
    () => options?.sort ?? [{ field: 'NAME' as const, order: 'ASC' as const }],
    [options?.sort]
  );
  const enabled = options?.enabled !== false;

  const filterKey = useMemo(() => JSON.stringify(filter), [filter]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        if (isFetched) {
          setIsRefetching(true);
        } else {
          setIsLoading(true);
        }

        const variables: useFeesRelayQuery$variables = {
          first: limit,
          filter: buildRelayFilter(filter),
          sort,
        };

        const result = await fetchQuery<UseFeesRelayDirectQuery>(
          relayEnvironment,
          FeesListDirectQuery,
          variables
        ).toPromise();

        if (cancelled) return;

        if (result) {
          const nodes =
            result.listFees?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];
          setData(nodes as FeeGraphQL[]);
          setCount(result.listFees?.count ?? undefined);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to fetch fees'));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
          setIsRefetching(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filterKey, limit, sort, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return {
    data,
    count,
    isLoading,
    isFetched,
    isRefetching,
    error,
    refetch,
  };
}

/**
 * Hook for fetching a single fee by ID with explicit loading state
 */
export function useFeeByIdRelayQuery(
  feeId: string | undefined,
  options?: { enabled?: boolean }
) {
  const [data, setData] = useState<FeeGraphQL | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const enabled = options?.enabled !== false && Boolean(feeId);

  useEffect(() => {
    if (!enabled || !feeId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        setIsLoading(true);

        const result = await fetchQuery<useFeesRelayByIdQuery>(
          relayEnvironment,
          FeeByIdQuery,
          { id: feeId }
        ).toPromise();

        if (cancelled) return;

        if (result) {
          setData(result.getFee as FeeGraphQL | null);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to fetch fee'));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, feeId, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return { data, isLoading, isFetched, error, refetch };
}

// ============================================================================
// Helpers
// ============================================================================

function buildRelayFilter(filter?: FeeQueryFilter): FeeFilterInput | undefined {
  if (!filter) return undefined;

  const relayFilter: FeeFilterInput = {};

  if (filter.systemId) {
    relayFilter.systemId = { eq: filter.systemId };
  }
  if (filter.organizationId) {
    relayFilter.organizationId = { eq: filter.organizationId };
  }
  if (filter.name) {
    if (filter.name.includes('%')) {
      relayFilter.name = { ilike: filter.name };
    } else {
      relayFilter.name = { ilike: `%${filter.name}%` };
    }
  }
  if (filter.archivedOn) {
    relayFilter.archivedOn = filter.archivedOn;
  }

  return Object.keys(relayFilter).length > 0 ? relayFilter : undefined;
}
